import React, { useState, useContext, useEffect } from "react";
import {
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Tab,
  Tabs,
  CircularProgress,
  Alert,
  Snackbar,
  Box,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import CoffeeIcon from "@mui/icons-material/Coffee";
import BakeryDiningIcon from "@mui/icons-material/BakeryDining";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { getMenu, addMenuItem, removeMenuItem } from "../../api/autenticoAPI";

const MenuManager = () => {
  const { user } = useContext(AuthContext);
  const [selectedMeal, setSelectedMeal] = useState(0);
  const [newItemName, setNewItemName] = useState("");
  const [menuItems, setMenuItems] = useState({
    breakfast: [],
    lunch: [],
    snack: [],
  });
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Carregar cardápio do banco ao montar o componente
  useEffect(() => {
    loadMenu();
  }, [user?.unit]);

  const loadMenu = async () => {
    if (!user?.unit) return;

    try {
      setLoading(true);
      const menu = await getMenu(user.unit);
      // Garantir ordenação alfabética em cada categoria
      const sortedMenu = {
        breakfast: [...menu.breakfast].sort((a, b) => 
          a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
        ),
        lunch: [...menu.lunch].sort((a, b) => 
          a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
        ),
        snack: [...menu.snack].sort((a, b) => 
          a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
        ),
      };
      setMenuItems(sortedMenu);
    } catch (error) {
      console.error("Erro ao carregar cardápio:", error);
      setSnackbar({
        open: true,
        message: "Erro ao carregar cardápio. Tente novamente.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getMealType = (index) => {
    switch (index) {
      case 0:
        return "breakfast";
      case 1:
        return "lunch";
      case 2:
        return "snack";
      default:
        return "breakfast";
    }
  };

  const handleAddItem = async () => {
    if (!newItemName.trim() || !user?.unit) return;

    const mealType = getMealType(selectedMeal);

    try {
      await addMenuItem(user.unit, mealType, newItemName.trim());
      
      // Recarregar menu completo para garantir ordenação correta
      await loadMenu();

      setNewItemName("");
      setSnackbar({
        open: true,
        message: "Item adicionado com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao adicionar item:", error);
      setSnackbar({
        open: true,
        message: "Erro ao adicionar item. Tente novamente.",
        severity: "error",
      });
    }
  };

  const handleRemoveItem = async (itemId) => {
    const mealType = getMealType(selectedMeal);

    try {
      await removeMenuItem(itemId);
      
      // Atualizar estado local
      setMenuItems((prev) => ({
        ...prev,
        [mealType]: prev[mealType].filter((item) => item.id !== itemId),
      }));

      setSnackbar({
        open: true,
        message: "Item removido com sucesso!",
        severity: "success",
      });
    } catch (error) {
      console.error("Erro ao remover item:", error);
      setSnackbar({
        open: true,
        message: "Erro ao remover item. Tente novamente.",
        severity: "error",
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <div style={{ padding: "24px", textAlign: "center" }}>
        <CircularProgress />
        <p style={{ marginTop: "16px", color: "#666" }}>Carregando cardápio...</p>
      </div>
    );
  }

  return (
    <div 
      style={{ 
        padding: "24px", 
        maxWidth: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
      }}
    >
        {/* Unidade */}
        <div
          style={{
            marginBottom: "20px",
            textAlign: "center",
            paddingBottom: "16px",
            borderBottom: "2px solid #e0e0e0",
          }}
        >
          <p
            style={{ margin: 0, fontWeight: "700", color: "#1e958c", fontSize: "18px", letterSpacing: "0.05em" }}
          >
            {user.unit}
          </p>
        </div>

        {/* Tabs de Navegação */}
        <Box sx={{ borderBottom: 2, borderColor: "#e0e0e0", mb: 3 }}>
          <Tabs
            value={selectedMeal}
            onChange={(_, newValue) => setSelectedMeal(newValue)}
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "15px",
                fontWeight: "600",
                minHeight: "56px",
                color: "#666",
                "&.Mui-selected": {
                  color: "#1e958c",
                  fontWeight: "700",
                },
              },
              "& .MuiTabs-indicator": {
                height: "3px",
                backgroundColor: "#1e958c",
              },
            }}
          >
            <Tab
              icon={<CoffeeIcon sx={{ fontSize: "20px" }} />}
              label="Café da Manhã"
              iconPosition="start"
            />
            <Tab
              icon={<RestaurantIcon sx={{ fontSize: "20px" }} />}
              label="Almoço"
              iconPosition="start"
            />
            <Tab
              icon={<BakeryDiningIcon sx={{ fontSize: "20px" }} />}
              label="Lanche"
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Input Area */}
        <div style={{ display: "flex", gap: "12px", marginBottom: "24px" }}>
          <TextField
            fullWidth
            size="medium"
            placeholder="Digite o nome do item..."
            value={newItemName}
            onChange={(e) => setNewItemName(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleAddItem()}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#fff",
              },
            }}
          />
          <IconButton
            onClick={handleAddItem}
            disabled={!newItemName.trim()}
            sx={{
              backgroundColor: newItemName.trim() ? "#1e958c" : "#e0e0e0",
              color: "white",
              borderRadius: "8px",
              padding: "10px",
              minWidth: "48px",
              "&:hover": {
                backgroundColor: newItemName.trim() ? "#0b4f6c" : "#e0e0e0",
              },
            }}
          >
            <AddIcon />
          </IconButton>
        </div>

        {/* Items List */}
        <List
          sx={{
            backgroundColor: "#f8fafc",
            borderRadius: "12px",
            border: "1px solid #e0e0e0",
            maxHeight: "500px",
            overflowY: "auto",
            flex: 1,
            minHeight: "300px",
            padding: "8px",
          }}
        >
          {menuItems[getMealType(selectedMeal)]?.length > 0 ? (
            menuItems[getMealType(selectedMeal)].map((item, index) => (
              <ListItem
                key={item.id}
                sx={{
                  borderBottom:
                    index !== menuItems[getMealType(selectedMeal)].length - 1
                      ? "1px solid #e0e0e0"
                      : "none",
                  borderRadius: "8px",
                  marginBottom: "4px",
                  "&:hover": {
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    style: {
                      color: "#333",
                      fontSize: "15px",
                      fontWeight: "500",
                    },
                  }}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveItem(item.id)}
                    sx={{ 
                      color: "#d32f2f",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.1)",
                      },
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary="Nenhum item cadastrado para esta refeição"
                primaryTypographyProps={{
                  style: {
                    color: "#999",
                    fontSize: "14px",
                    fontStyle: "italic",
                    textAlign: "center",
                  },
                }}
              />
            </ListItem>
          )}
        </List>

        {/* Snackbar para feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </div>
  );
};

export default MenuManager;
